import {
  cloneElement,
  createElement,
  forwardRef,
  isValidElement,
  ComponentPropsWithRef,
  useMemo
} from 'react'

import { trueOrUndefined } from '@trustycars/genie-ui/functions'

import { useRouteSummary } from '$contexts/Router'
import useRouter from '$actions/useRouter'
import { redirect } from '$app/router'
import { buildURL, valueOrUndefined } from '$app/utilities'

type RedirectParams = Parameters<typeof redirect>

type RouteNames = RedirectParams[0]
type RedirectOptions = RedirectParams[1]

type Tag = ComponentPropsWithRef<'a'>

export type LinkProps = {
  to: RouteNames
  options?: RedirectOptions
} & Omit<Tag, 'href'>

export const Link = forwardRef<Tag, LinkProps>(
  (
    {
      children,
      to,
      options,
      onClick: onNativeClick = () => undefined,
      ...props
    },
    ref
  ) => {
    const isJSX = isValidElement(children)

    const { routeData, details } = useRouteSummary()
    const { params, hash, queryParams } = useRouter()
    const { pathname } = routeData[to]
    const { isMatch, meta } = details[to]
    const { title } = { ...meta }

    const { href, redirectOptions } = useMemo(() => {
      const redirectOptions = (() => {
        if (typeof options == 'function') {
          return options({
            hash,
            params,
            queryParams
          })
        }

        return options
      })()

      const href = buildURL(pathname, redirectOptions).toString()

      return {
        href,
        redirectOptions
      }
    }, [pathname, params, hash, queryParams, options])

    const onClick: LinkProps['onClick'] = e => {
      onNativeClick(e)

      if (e.defaultPrevented) return

      e.preventDefault()

      redirect(to, redirectOptions)
    }

    const passProps = {
      href,
      onClick,
      ref,
      'data-active': trueOrUndefined(isMatch),
      title: valueOrUndefined(isJSX, title),
      ...props
    }

    if (isJSX) return cloneElement(children, passProps)

    return createElement('a', { children, ...passProps })
  }
)

export default Link
